import "./App.css";
import Month from "./components/Month";
import Scoreboard from "./components/Scoreboard";
import AdminLogin from "./components/AdminLogin";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import { NameModal } from "./components/NameModal";
import { useAuth } from "./context/AuthContext";

const supabase = createClient(
  "https://vzhywrjmvskqjqmbwtlt.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ6aHl3cmptdnNrcWpxbWJ3dGx0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MDQxMTg0OTEsImV4cCI6MjAxOTY5NDQ5MX0.VfP050kmMS5WzqFLE8z1GkHJfwkC5Spu1ujRqObp5W0",
);

function App() {
  const { isAdmin } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [scoreDict, setScoreDict] = useState({});
  const [gamesDict, setGamesDict] = useState({});
  const [gamesPlayedDict, setGamesPlayedDict] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  // Secret gesture: tap title 5 times to reveal admin login
  const tapCountRef = useRef(0);
  const tapTimeoutRef = useRef(null);

  const handleTitleTap = () => {
    tapCountRef.current += 1;

    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }

    if (tapCountRef.current >= 5) {
      setShowAdminLogin(true);
      tapCountRef.current = 0;
    } else {
      tapTimeoutRef.current = setTimeout(() => {
        tapCountRef.current = 0;
      }, 2000);
    }
  };

  // Fetch available years from Supabase
  useEffect(() => {
    supabase
      .from("games")
      .select("year")
      .then((response) => {
        if (response.data) {
          const years = response.data
            .map((row) => parseInt(row.year))
            .sort((a, b) => b - a); // Sort descending (newest first)
          setAvailableYears(years);
        }
      });
  }, []);

  // Auto-refresh every 15 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      window.location.reload();
    }, 900000);

    return () => clearInterval(interval);
  }, []);

  // Pull-to-refresh functionality
  useEffect(() => {
    let startY = 0;
    let isPulling = false;

    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].pageY;
        isPulling = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!isPulling) return;
      const currentY = e.touches[0].pageY;
      const diff = currentY - startY;

      if (diff > 80 && window.scrollY === 0) {
        setIsRefreshing(true);
      }
    };

    const handleTouchEnd = () => {
      if (isRefreshing) {
        window.location.reload();
      }
      isPulling = false;
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isRefreshing]);

  useEffect(() => {
    setIsLoading(true);
    supabase
      .from("games")
      .select()
      .eq("year", String(selectedYear))
      .then((response) => {
        if (!response.data || response.data.length === 0) {
          setGames([]);
          setPlayers([]);
          setScoreDict({});
          setGamesDict({});
          setGamesPlayedDict({});
          setIsLoading(false);
          return;
        }

        const games = response.data[0].games;
        setGames(games);

        const players = Array.from(
          new Set(
            games.flatMap((month) => month.games.flatMap((game) => game.teams)),
          ),
        );
        setPlayers(players);

        const tempScoreDict = {};

        games.forEach((month) => {
          month.games.forEach((game) => {
            game.teams.forEach((team, index) => {
              // Extracting individual names from team string
              let names = team.split("(");
              names.forEach((name) => {
                name = name.replace(")", "").trim(); // Remove the closing parenthesis if any
                if (!tempScoreDict[name]) tempScoreDict[name] = 0; // Initialize the person's point if not done yet
                tempScoreDict[name] += game.score[index]; // Add the score to the person's total points
              });
            });
          });
        });
        setScoreDict(tempScoreDict);

        const tempGamesDict = {};
        games.forEach((month) => {
          month.games.forEach((game) => {
            game.teams.forEach((team, index) => {
              // Extracting individual names from team string
              let names = team.split("(");
              names.forEach((name) => {
                name = name.replace(")", "").trim(); // Remove the closing parenthesis if any
                if (!tempGamesDict[name]) tempGamesDict[name] = 0; // Initialize the person's point if not done yet
                tempGamesDict[name] += 1; // Add the score to the person's total points
              });
            });
          });
        });
        setGamesDict(tempGamesDict);

        // Calculate games actually played (up to current date)
        const tempGamesPlayedDict = {};
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today

        games.forEach((month) => {
          // Extract year from month string (e.g., "Januari 2026" -> 2026)
          const yearMatch = month.month.match(/\d{4}/);
          const year = yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear();

          month.games.forEach((game) => {
            // Parse date like "05 Jan" with the year from month
            const gameDate = new Date(`${game.date} ${year}`);

            // Only count if game date is in the past or today
            if (gameDate <= today) {
              game.teams.forEach((team) => {
                let names = team.split("(");
                names.forEach((name) => {
                  name = name.replace(")", "").trim();
                  if (!tempGamesPlayedDict[name]) tempGamesPlayedDict[name] = 0;
                  tempGamesPlayedDict[name] += 1;
                });
              });
            }
          });
        });
        setGamesPlayedDict(tempGamesPlayedDict);
        setIsLoading(false);
      });
  }, [selectedYear]);

  // Compute per-game historical scoreDict/gamesPlayedDict snapshots.
  // For each game, the snapshot contains only scores from games played before that date,
  // so that team pairings reflect the ranking at the time of each match.
  const historicalDicts = useMemo(() => {
    if (!games || games.length === 0) return {};

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    // Flatten all games with coordinates and parsed dates
    const flatGames = [];
    games.forEach((month, monthIndex) => {
      const yearMatch = month.month.match(/\d{4}/);
      const year = yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear();

      month.games.forEach((game, gameIndex) => {
        const gameDate = new Date(`${game.date} ${year}`);
        flatGames.push({ monthIndex, gameIndex, game, gameDate });
      });
    });

    // Sort chronologically
    flatGames.sort((a, b) => a.gameDate - b.gameDate);

    // Build up dicts incrementally, snapshotting before each game
    const runningScoreDict = {};
    const runningGamesPlayedDict = {};
    const result = {};

    for (const entry of flatGames) {
      const key = `${entry.monthIndex}-${entry.gameIndex}`;
      // Snapshot the state BEFORE this game's scores are added
      result[key] = {
        scoreDict: { ...runningScoreDict },
        gamesPlayedDict: { ...runningGamesPlayedDict },
      };

      // Only accumulate scores from games that have been played (past or today)
      if (entry.gameDate <= today) {
        entry.game.teams.forEach((team, index) => {
          let names = team.split("(");
          names.forEach((name) => {
            name = name.replace(")", "").trim();
            if (!runningScoreDict[name]) runningScoreDict[name] = 0;
            runningScoreDict[name] += entry.game.score[index];
            if (!runningGamesPlayedDict[name]) runningGamesPlayedDict[name] = 0;
            runningGamesPlayedDict[name] += 1;
          });
        });
      }
    }

    return result;
  }, [games]);

  // Update game score function for admin
  const updateGameScore = useCallback(async (monthIndex, gameIndex, playerIndex, newScore) => {
    // Create deep copy of games
    const updatedGames = JSON.parse(JSON.stringify(games));

    // Update the score
    updatedGames[monthIndex].games[gameIndex].score[playerIndex] = Math.max(0, newScore);

    // Optimistic update
    setGames(updatedGames);

    // Recalculate score dict
    const tempScoreDict = {};
    updatedGames.forEach((month) => {
      month.games.forEach((game) => {
        game.teams.forEach((team, index) => {
          let names = team.split("(");
          names.forEach((name) => {
            name = name.replace(")", "").trim();
            if (!tempScoreDict[name]) tempScoreDict[name] = 0;
            tempScoreDict[name] += game.score[index];
          });
        });
      });
    });
    setScoreDict(tempScoreDict);

    // Save to Supabase
    try {
      const { error } = await supabase
        .from("games")
        .update({ games: updatedGames })
        .eq("year", String(selectedYear));

      if (error) {
        console.error("Error updating score:", error);
        // Could revert here if needed
      }
    } catch (err) {
      console.error("Error updating score:", err);
    }
  }, [games, selectedYear]);

  // Update player name function for admin
  const updatePlayerName = useCallback(async (monthIndex, gameIndex, playerIndex, newName) => {
    // Create deep copy of games
    const updatedGames = JSON.parse(JSON.stringify(games));

    // Update the player name
    updatedGames[monthIndex].games[gameIndex].teams[playerIndex] = newName;

    // Optimistic update
    setGames(updatedGames);

    // Recalculate all dictionaries
    const tempScoreDict = {};
    const tempGamesDict = {};
    const tempGamesPlayedDict = {};
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    updatedGames.forEach((month) => {
      const yearMatch = month.month.match(/\d{4}/);
      const year = yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear();

      month.games.forEach((game) => {
        const gameDate = new Date(`${game.date} ${year}`);

        game.teams.forEach((team, index) => {
          let names = team.split("(");
          names.forEach((name) => {
            name = name.replace(")", "").trim();
            if (!tempScoreDict[name]) tempScoreDict[name] = 0;
            if (!tempGamesDict[name]) tempGamesDict[name] = 0;
            tempScoreDict[name] += game.score[index];
            tempGamesDict[name] += 1;

            if (gameDate <= today) {
              if (!tempGamesPlayedDict[name]) tempGamesPlayedDict[name] = 0;
              tempGamesPlayedDict[name] += 1;
            }
          });
        });
      });
    });

    setScoreDict(tempScoreDict);
    setGamesDict(tempGamesDict);
    setGamesPlayedDict(tempGamesPlayedDict);

    // Update players list
    const newPlayers = Array.from(
      new Set(
        updatedGames.flatMap((month) => month.games.flatMap((game) => game.teams)),
      ),
    );
    setPlayers(newPlayers);

    // Save to Supabase
    try {
      const { error } = await supabase
        .from("games")
        .update({ games: updatedGames })
        .eq("year", String(selectedYear));

      if (error) {
        console.error("Error updating player:", error);
      }
    } catch (err) {
      console.error("Error updating player:", err);
    }
  }, [games, selectedYear]);

  const [isScoreboardVisible, setIsScoreboardVisible] = useState(false);
  const closeScoreBoard = () => {
    setIsScoreboardVisible(false);
  };

  return (
    <div className="App">
      {/* Pull-to-refresh indicator */}
      <div className={`pull-indicator ${isRefreshing ? 'visible' : ''}`}>
        Vernieuwen...
      </div>

      <div className="header-title" onClick={handleTitleTap}>
        <h1>De Padel Club</h1>
        {isAdmin && <span className="admin-indicator"></span>}
      </div>

      {/* Admin login modal */}
      {showAdminLogin && (
        <AdminLogin onClose={() => setShowAdminLogin(false)} />
      )}

      <div id="portal"></div>

      <button
        className="scoreboard-toggle"
        onClick={() => setIsScoreboardVisible(!isScoreboardVisible)}
      >
        Scorebord
      </button>

      {/* Year dropdown - fixed bottom right */}
      <div className="year-dropdown-container">
        <select
          className="year-dropdown"
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Backdrop for mobile scoreboard */}
      {isScoreboardVisible && (
        <div className="scoreboard-backdrop" onClick={closeScoreBoard} />
      )}

      {isScoreboardVisible && players.length && scoreDict && gamesDict && (
        <Scoreboard
          players={players}
          handleClose={closeScoreBoard}
          scoreDict={scoreDict}
          gamesDict={gamesDict}
          gamesPlayedDict={gamesPlayedDict}
        />
      )}

      {isLoading ? (
        <div className="loading">Laden...</div>
      ) : games.length > 0 ? (
        games.map((month, index) => (
          <Month
            key={index}
            monthData={month}
            monthIndex={index}
            isAdmin={isAdmin}
            onScoreUpdate={updateGameScore}
            onPlayerUpdate={updatePlayerName}
            scoreDict={scoreDict}
            gamesPlayedDict={gamesPlayedDict}
            historicalDicts={historicalDicts}
          />
        ))
      ) : (
        <div className="no-games">Geen wedstrijden gevonden voor {selectedYear}</div>
      )}
    </div>
  );
}

export default App;
